import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { MarkdownText } from '@/components/performance/MarkdownText';
import { EnumBadge } from '@/components/ui/enum-badge';
import type { OmlEntityType, OmlFieldDef } from '../../../../shared/types/oml';
import { formatFieldValue, statusTone } from './oml-ui';
import { AssetPreview } from './AssetField';
import { LockedField } from './LockedField';
import { fieldLabel } from './oml-ui';

export interface ValueCtx {
  accountId: string;
  names?: Record<string, string>;
  relationNames?: Record<string, string>;
  locked?: ReadonlySet<string>;
  type: OmlEntityType;
}

/** Un valor de campo en modo lectura, según su tipo del schema. */
export function FieldValue({ def, value, ctx, compact = false }: { def: OmlFieldDef; value: unknown; ctx: ValueCtx; compact?: boolean }) {
  const { t } = useTranslation();
  if (ctx.locked?.has(def.key)) return <LockedField label={fieldLabel(t, ctx.type, def.key)} accountId={ctx.accountId} field={def.key} requestable={def.restricted !== 'credential'} compact={compact} />;
  if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) return <span className="text-muted-foreground">—</span>;
  if (def.isStatus || def.key === 'accessType' || def.key === 'measurementStatus') return <EnumBadge value={String(value)} tone={statusTone(value)} />;
  if (def.type === 'richtext') return compact ? <span className="line-clamp-2">{String(value)}</span> : <MarkdownText className="text-sm">{String(value)}</MarkdownText>;
  if (def.type === 'link') {
    const href = String(value);
    return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline break-all"><ExternalLink className="h-3.5 w-3.5 shrink-0" />{compact ? href.replace(/^https?:\/\//, '').slice(0, 40) : href}</a>;
  }
  if (def.type === 'file') return <AssetPreview accountId={ctx.accountId} assetId={String(value)} compact={compact} />;
  if (def.type === 'files' && Array.isArray(value)) return <div className="flex flex-wrap gap-2">{value.map((id) => <AssetPreview key={String(id)} accountId={ctx.accountId} assetId={String(id)} compact />)}</div>;
  if (def.type === 'multiselect' && Array.isArray(value) && !compact) return <div className="flex flex-wrap gap-1">{value.map((v) => <EnumBadge key={String(v)} value={String(v)} tone="neutral" />)}</div>;
  if (def.type === 'bool') return <EnumBadge value={value ? t('common.yes', 'Sí') : t('common.no', 'No')} tone={value ? 'success' : 'muted'} />;
  return <span className={compact ? 'truncate' : ''}>{formatFieldValue(t, def, value, ctx)}</span>;
}
